import React from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Stack,
  IconButton,
  Backdrop,
  Fade,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import { Icons } from "../../../../assets/icon";
import dayjs from "dayjs";
import CustomDatePicker from "../../../../../components/DatePicker/CustomDatePicker";
import CustomTimePicker from "../../../../../components/TimePicker/SlideTimePicker";
import CustomTextField from "../../../../../components/common/CustomTextField";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import { useBookStage } from "../../../../../services/stagemode_api/venue/venue_generates_qr";

interface BookingFormValues {
  stage_card_id: number;
  venue_name: string;
  venue_size: "Small" | "Medium" | "Large" | "Broadcast";
  venue_location: string;
  message: string;
  start_date_time: string;
  contact_email: string;
}

const validationSchema = Yup.object({
  venue_name: Yup.string().required("Venue name is required"),
  venue_size: Yup.string().required("Venue size is required"),
  venue_location: Yup.string().required("Venue location is required"),
  start_date_time: Yup.string().required("Date and time is required"),
  contact_email: Yup.string()
    .email("Invalid email")
    .required("Email is required"),
});

interface BookingModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  title: string;
  ticketPrice: number;
  userBalance?: number;
  startDateTime?: string;
  onBookingSuccess: () => void;
  venueSize: string;
}

const BookingModal: React.FC<BookingModalProps> = ({
  open,
  onClose,
  eventId,
  title,
  ticketPrice,
  userBalance = 100,
  startDateTime,
  onBookingSuccess,
  venueSize,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const bookStageMutation = useBookStage();

  const formik = useFormik<BookingFormValues>({
    initialValues: {
      stage_card_id: Number(eventId),
      venue_name: "",
      venue_size: venueSize as "Small" | "Medium" | "Large" | "Broadcast",
      venue_location: "",
      message: "",
      start_date_time: "",
      contact_email: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await bookStageMutation.mutateAsync(values);
        onBookingSuccess();
        onClose();
      } catch (error) {
        console.error("Booking failed:", error);
      }
    },
  });

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      const currentTime = formik.values.start_date_time
        ? dayjs(formik.values.start_date_time)
        : dayjs();

      const newDateTime = date
        .hour(currentTime.hour())
        .minute(currentTime.minute())
        .second(0)
        .millisecond(0);

      formik.setFieldValue("start_date_time", newDateTime.toISOString());
    }
  };

  const handleTimeChange = (time: dayjs.Dayjs | null) => {
    if (time) {
      const currentDate = formik.values.start_date_time
        ? dayjs(formik.values.start_date_time)
        : dayjs();

      const newDateTime = currentDate
        .hour(time.hour())
        .minute(time.minute())
        .second(0)
        .millisecond(0);

      formik.setFieldValue("start_date_time", newDateTime.toISOString());
    }
  };

  // ——— Netflix-ish tokens (design only)
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.78)",
    border: "rgba(255,255,255,0.12)",
    red: "#E50914",
    redHover: "#b20710",
    glass: "rgba(255,255,255,0.06)",
  };

  const inputFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: COLORS.bg,
      color: COLORS.text,
      borderRadius: 2,
      minHeight: { xs: 48, sm: 40 },
      "& fieldset": { borderColor: COLORS.border },
      "&:hover fieldset": { borderColor: COLORS.muted },
      "&.Mui-focused fieldset": { borderColor: COLORS.red },
    },
    "& .MuiInputBase-input": {
      fontSize: 16,
      "&::placeholder": { color: "rgba(255,255,255,0.6)", opacity: 1 },
    },
  };

  const modalContainerSx = {
    position: "relative",
    width: isMobile ? "100vw" : { xs: "calc(100vw - 32px)", sm: 520, md: 560 },
    maxWidth: isMobile ? "100vw" : "calc(100vw - 32px)",
    mx: isMobile ? 0 : "auto",
    my: isMobile ? 0 : { xs: 2, sm: 3 },
    mt: isMobile ? 0 : { xs: "5vh", sm: "10vh", md: "12vh" },
    mb: isMobile ? 0 : { xs: 2, sm: 3 },
    height: isMobile ? "100vh" : "auto",
    maxHeight: isMobile ? "100vh" : { xs: "90vh", sm: "85vh" },
    borderRadius: isMobile ? 0 : 3,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    bgcolor: COLORS.panel,
    color: COLORS.text,
    border: `1px solid ${COLORS.border}`,
    boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-track": { bgcolor: COLORS.bg },
    "&::-webkit-scrollbar-thumb": { borderRadius: 3, bgcolor: COLORS.border },
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 300,
          sx: { backgroundColor: "rgba(0,0,0,0.85)" }, // darker backdrop
        },
      }}
    >
      <Fade in={open}>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 36, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          sx={modalContainerSx}
        >
          <Box
            sx={{
              overflowY: "auto",
              overflowX: "hidden",
              flex: 1,
              minHeight: 0,
              p: { xs: 2, sm: 2.5, md: 3 },
              pt: { xs: 3, sm: 3.5 },
            }}
          >
          {/* subtle top accent */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: COLORS.red,
            }}
          />

          <FormikProvider value={formik}>
            {/* Close Icon */}
            <IconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                color: COLORS.muted,
                "&:hover": { color: COLORS.text, backgroundColor: COLORS.glass },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Header */}
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <EventIcon sx={{ color: COLORS.red }} />
              <Typography variant="h6" fontWeight={900} sx={{ letterSpacing: "-0.01em" }}>
                Book Event
              </Typography>
            </Stack>

            {/* Content */}
            <Typography variant="body2" sx={{ color: COLORS.muted }} mb={1}>
              You're about to book a ticket for
            </Typography>

            <Typography
              fontWeight={800}
              fontSize={16}
              mb={2}
              sx={{ color: COLORS.text, letterSpacing: "-0.01em" }}
            >
              {title || eventId}
            </Typography>

            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                {/* Venue Details — stacked on mobile, side-by-side on tablet+ */}
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        name="venue_name"
                        placeholder="Venue Name"
                        value={formik.values.venue_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        errorMessage={
                          formik.touched.venue_name && formik.errors.venue_name
                        }
                        sx={inputFieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        fullWidth
                        name="venue_location"
                        placeholder="Venue Location"
                        value={formik.values.venue_location}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        errorMessage={
                          formik.touched.venue_location &&
                          formik.errors.venue_location
                        }
                        sx={inputFieldSx}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Date & Time — stacked on mobile, side-by-side on tablet+ */}
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <CustomDatePicker
                        name="date"
                        value={
                          formik.values.start_date_time
                            ? dayjs(formik.values.start_date_time)
                            : null
                        }
                        onChange={handleDateChange}
                        minDate={dayjs().startOf("day")}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.bg,
                            color: COLORS.text,
                            borderRadius: 2,
                            "& fieldset": { borderColor: COLORS.border },
                            "&:hover fieldset": { borderColor: COLORS.muted },
                            "&.Mui-focused fieldset": { borderColor: COLORS.red },
                          },
                          "& .MuiSvgIcon-root": { color: COLORS.muted },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTimePicker
                        name="time"
                        timeSteps={{ minutes: 1 }}
                        minTime={
                          dayjs(formik.values.start_date_time).isToday()
                            ? dayjs()
                            : undefined
                        }
                        onChange={handleTimeChange}
                        value={
                          formik.values.start_date_time
                            ? dayjs(formik.values.start_date_time)
                            : null
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.bg,
                            color: COLORS.text,
                            borderRadius: 2,
                            "& fieldset": { borderColor: COLORS.border },
                            "&:hover fieldset": { borderColor: COLORS.muted },
                            "&.Mui-focused fieldset": { borderColor: COLORS.red },
                          },
                          "& .MuiSvgIcon-root": { color: COLORS.muted },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Contact Information */}
                <Grid item xs={12}>
                  <CustomTextField
                    fullWidth
                    name="contact_email"
                    placeholder="Contact Email"
                    value={formik.values.contact_email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                        errorMessage={
                          formik.touched.contact_email &&
                          formik.errors.contact_email
                        }
                        sx={inputFieldSx}
                      />
                </Grid>

                {/* Message */}
                <Grid item xs={12}>
                  <CustomTextField
                    fullWidth
                    multiline
                    rows={3}
                    name="message"
                    placeholder="Add a message (optional)"
                    value={formik.values.message}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorMessage={
                      formik.touched.message && formik.errors.message
                    }
                    sx={inputFieldSx}
                  />
                </Grid>

                {/* Price Information */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={800}
                    mb={1}
                    sx={{ color: COLORS.text, letterSpacing: "-0.01em" }}
                  >
                    Price Details
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2" sx={{ color: COLORS.muted }}>
                      Ticket Price:{" "}
                      <strong style={{ color: COLORS.red }}>
                        <Icons.Coin /> {ticketPrice}
                      </strong>
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.muted }}>
                      Your Balance:{" "}
                      <strong style={{ color: COLORS.text }}>
                        <Icons.Coin /> {userBalance}
                      </strong>
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2, borderColor: COLORS.border }} />

              {/* Actions — stacked full-width on mobile for easy tap, row on desktop */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="flex-end"
                alignItems="stretch"
                sx={{ gap: 2 }}
              >
                <Button
                  variant="outlined"
                  onClick={onClose}
                  disabled={formik.isSubmitting || bookStageMutation.isLoading}
                  fullWidth={isMobile}
                  sx={{
                    fontFamily: "Lexend",
                    fontWeight: 800,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.5, sm: 1.25 },
                    minHeight: { xs: 48, sm: 40 },
                    borderRadius: 2,
                    textTransform: "none",
                    borderColor: COLORS.border,
                    color: COLORS.text,
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.06)",
                      borderColor: COLORS.muted,
                    },
                  }}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth={isMobile}
                  disabled={
                    formik.isSubmitting ||
                    bookStageMutation.isLoading ||
                    userBalance < ticketPrice
                  }
                  sx={{
                    fontFamily: "Lexend",
                    fontWeight: 900,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.5, sm: 1.25 },
                    minHeight: { xs: 48, sm: 40 },
                    borderRadius: 2,
                    textTransform: "none",
                    backgroundColor: COLORS.red,
                    color: "#fff",
                    boxShadow: "0 10px 24px rgba(229,9,20,0.35)",
                    "&:hover": {
                      backgroundColor: COLORS.redHover,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "rgba(255,255,255,0.24)",
                      color: "rgba(255,255,255,0.7)",
                    },
                  }}
                >
                  {formik.isSubmitting || bookStageMutation.isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Book Now"
                  )}
                </Button>
              </Stack>
            </form>
          </FormikProvider>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default BookingModal;
